/**
 * ConfigureTaskDialog — Modal moderno per configurare un turno pulizia.
 *
 * Permette di settare in un colpo solo:
 *  - num_guests (stepper +/-)
 *  - room_id (se la struttura è a stanze e non è ancora assegnata)
 *  - extras: culla, lettino da viaggio, letti divisi (toggle chips)
 *  - notes (opzionale, breve)
 *
 * Il salvataggio è atomico: il bottone "Fatto" grande + verde con
 * animazione manda una PATCH unica al backend.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, X, Plus, Minus, Bed, Baby, Suitcase, Scissors } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXTRAS = [
  { id: 'culla', label: 'Culla', icon: Baby, color: 'rose' },
  { id: 'lettino_viaggio', label: 'Lettino da viaggio', icon: Suitcase, color: 'amber' },
  { id: 'letti_divisi', label: 'Dividere i letti', icon: Scissors, color: 'blue' },
];

export default function ConfigureTaskDialog({ task, prop, rooms, onClose, onSaved }) {
  const [guests, setGuests] = useState(task?.num_guests || 2);
  const [roomId, setRoomId] = useState(task?.room_id || '');
  const [extras, setExtras] = useState(task?.extras || []);
  const [notes, setNotes] = useState(task?.notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const isStanze = prop?.cleaning_type === 'stanze';
  const propRooms = rooms.filter(r => r.property_id === task?.property_id);

  useEffect(() => {
    setGuests(task?.num_guests || 2);
    setRoomId(task?.room_id || '');
    setExtras(task?.extras || []);
    setNotes(task?.notes || '');
  }, [task]);

  const toggleExtra = (id) => {
    setExtras(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSave = async () => {
    if (isStanze && !roomId) {
      toast.error('Seleziona la stanza');
      return;
    }
    if (!guests || guests < 1) {
      toast.error('Numero ospiti non valido');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        num_guests: Number(guests),
        extras,
        notes,
      };
      if (isStanze) payload.room_id = roomId;
      await axios.patch(`${API}/cleaning/tasks/${task.id}`, payload);
      setSaved(true);
      // Animation: green pulse for 700ms then close
      setTimeout(() => {
        onSaved?.({ ...task, ...payload, room_id: payload.room_id ?? task.room_id, assignment_required: payload.room_id ? false : task.assignment_required });
        onClose();
        toast.success('Configurazione salvata');
      }, 700);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore salvataggio');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4 backdrop-blur-sm" data-testid="configure-task-dialog">
      <div className="bg-white w-full sm:max-w-md max-h-[94vh] overflow-y-auto rounded-t-2xl sm:rounded-none animate-in slide-in-from-bottom duration-200">
        {/* Header */}
        <div className="sticky top-0 px-5 py-4 border-b border-zinc-200 flex items-center justify-between bg-white">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700">Configura turno</p>
            <h3 className="text-base font-black tracking-tight text-zinc-900 leading-tight mt-0.5">
              {prop?.name || 'Struttura'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100" data-testid="close-configure-dialog" aria-label="Chiudi">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Stanza (solo se serve) */}
          {isStanze && (
            <div>
              <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 flex items-center gap-1.5 mb-2">
                <Bed size={12} weight="bold" /> Stanza
              </label>
              <select
                value={roomId}
                onChange={(e) => setRoomId(e.target.value)}
                className="w-full h-11 px-3 border-2 border-zinc-300 focus:border-emerald-600 text-sm focus:outline-none"
                data-testid="configure-room-select"
              >
                <option value="">— seleziona stanza —</option>
                {propRooms.map(r => (
                  <option key={r.id} value={r.id}>{r.name} · max {r.max_occupancy}</option>
                ))}
              </select>
            </div>
          )}

          {/* Ospiti stepper */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 mb-2 block">N° ospiti</label>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => setGuests(g => Math.max(1, Number(g) - 1))}
                className="w-12 h-12 flex items-center justify-center border-2 border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50 active:scale-95 transition-all"
                aria-label="Diminuisci"
                data-testid="guests-decrement"
              >
                <Minus size={18} weight="bold" />
              </button>
              <input
                type="number"
                min="1"
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value) || 1)}
                className="w-20 h-12 text-center text-2xl font-black tabular-nums border-2 border-zinc-300 focus:border-emerald-600 focus:outline-none"
                data-testid="guests-input"
              />
              <button
                onClick={() => setGuests(g => Math.min(20, Number(g) + 1))}
                className="w-12 h-12 flex items-center justify-center border-2 border-zinc-300 hover:border-zinc-900 hover:bg-zinc-50 active:scale-95 transition-all"
                aria-label="Aumenta"
                data-testid="guests-increment"
              >
                <Plus size={18} weight="bold" />
              </button>
            </div>
          </div>

          {/* Extras */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 mb-2 block">Extra preparazione</label>
            <div className="grid grid-cols-1 gap-2">
              {EXTRAS.map(ex => {
                const active = extras.includes(ex.id);
                const colorMap = {
                  rose: active ? 'border-rose-500 bg-rose-50 text-rose-700' : 'border-zinc-200 text-zinc-500 hover:border-rose-300',
                  amber: active ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-zinc-200 text-zinc-500 hover:border-amber-300',
                  blue: active ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-zinc-200 text-zinc-500 hover:border-blue-300',
                };
                return (
                  <button
                    key={ex.id}
                    onClick={() => toggleExtra(ex.id)}
                    className={`flex items-center justify-between gap-3 p-3 border-2 transition-all text-left active:scale-[0.98] ${colorMap[ex.color]}`}
                    data-testid={`extra-${ex.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <ex.icon size={20} weight={active ? 'fill' : 'bold'} />
                      <span className="text-sm font-bold">{ex.label}</span>
                    </div>
                    <div className={`w-5 h-5 border-2 flex items-center justify-center transition-all ${active ? 'border-current bg-current' : 'border-zinc-300'}`}>
                      {active && <CheckCircle size={14} weight="fill" className="text-white" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes (compact) */}
          <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-zinc-600 mb-2 block">Note staff (opzionale)</label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              maxLength={300}
              className="w-full border-2 border-zinc-300 p-2.5 text-sm resize-none focus:border-emerald-600 focus:outline-none"
              placeholder="Es. ospite arriva tardi · richiede cambio asciugamani"
              data-testid="configure-notes"
            />
          </div>
        </div>

        {/* Footer big green FATTO button */}
        <div className="sticky bottom-0 px-5 py-4 border-t border-zinc-200 bg-white">
          <button
            onClick={handleSave}
            disabled={saving}
            className={`w-full py-4 text-white text-sm font-black uppercase tracking-[0.15em] transition-all relative overflow-hidden ${
              saved
                ? 'bg-emerald-600 scale-[1.02]'
                : 'bg-emerald-600 hover:bg-emerald-700 active:scale-[0.98]'
            } disabled:opacity-90`}
            data-testid="configure-save-btn"
          >
            <span className={`inline-flex items-center justify-center gap-2 transition-all ${saved ? 'opacity-0' : 'opacity-100'}`}>
              {saving ? 'Salvataggio…' : <>Fatto <CheckCircle size={16} weight="bold" /></>}
            </span>
            {saved && (
              <span className="absolute inset-0 flex items-center justify-center gap-2 animate-in zoom-in fade-in duration-200">
                <CheckCircle size={22} weight="fill" className="text-white" />
                <span>Salvato!</span>
              </span>
            )}
            {saved && (
              <span className="absolute inset-0 bg-white/20 animate-ping rounded-none" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
